import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui'
import { Pagination } from '@/components/ui/pagination'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { usePagination, useWorkflowScheduleOccurrences } from '@/hooks'
import {
  WORKFLOW_SCHEDULE_OCCURRENCE_REASON_LABELS,
  WORKFLOW_SCHEDULE_OCCURRENCE_FILTER_STATUSES,
  type WorkflowScheduleOccurrenceFilters,
  type WorkflowScheduleOccurrenceStatus,
} from '@/types'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { OccurrenceStatusBadge } from './OccurrenceStatusBadge'
import { formatInTimeZone } from './scheduleFormat'

const ALL_STATUSES = 'all'
const CAUGHT_UP = 'caughtUp'

type HistoryFilter = WorkflowScheduleOccurrenceStatus | typeof ALL_STATUSES | typeof CAUGHT_UP

function toOccurrenceFilters(filter: HistoryFilter): WorkflowScheduleOccurrenceFilters {
  if (filter === ALL_STATUSES) return {}
  if (filter === CAUGHT_UP) return { reason: 'CaughtUp' }
  return { status: filter }
}

interface Props {
  projectId: string
  scheduleId: string
  timeZoneId: string
}

export function ScheduleHistoryTable({ projectId, scheduleId, timeZoneId }: Props) {
  const navigate = useNavigate()
  const [status, setStatus] = useState<HistoryFilter>(ALL_STATUSES)
  const { page, pageSize, pageSizeOptions, setPage, setPageSize, resetPage, paginationParams } = usePagination({ defaultPageSize: 20 })

  const filters = toOccurrenceFilters(status)
  const { data, isLoading, isError, refetch } = useWorkflowScheduleOccurrences(projectId, scheduleId, filters, paginationParams)
  const occurrences = data?.items ?? []
  const totalCount = data?.totalCount ?? 0

  const handleStatusChange = (value: string) => {
    if (!value) return
    setStatus(value as HistoryFilter)
    resetPage()
  }

  return (
    <section className="space-y-4" aria-labelledby="schedule-history-heading" data-testid="schedule-history">
      <div className="flex items-end justify-between gap-4">
        <h2 id="schedule-history-heading" className="text-xl font-bold">History</h2>
        <div className="flex flex-col gap-1.5 w-48">
          <Label htmlFor="schedule-history-status">Status</Label>
          <Select value={status} onValueChange={handleStatusChange}>
            <SelectTrigger id="schedule-history-status" aria-label="Filter history by status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_STATUSES}>All statuses</SelectItem>
              {WORKFLOW_SCHEDULE_OCCURRENCE_FILTER_STATUSES.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
              <SelectItem value={CAUGHT_UP}>Caught up</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isError ? (
        <div className="text-center py-12 text-muted-foreground" role="alert" data-testid="schedule-history-error">
          Failed to load the schedule history.{' '}
          <button type="button" className="underline" onClick={() => refetch()}>Retry</button>
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground" role="status" aria-live="polite" data-testid="schedule-history-loading">
          <Loader2 className="h-5 w-5 animate-spin mr-2" aria-hidden="true" />
          Loading history…
        </div>
      ) : occurrences.length === 0 ? (
        <div className="rounded-lg border border-dashed py-12 text-center text-muted-foreground" data-testid="schedule-history-empty">
          {status === ALL_STATUSES
            ? 'No occurrences yet. They appear here once the schedule comes due.'
            : 'No occurrences match this status.'}
        </div>
      ) : (
        <>
          <div className="rounded-lg border" data-testid="schedule-history-table">
            <Table aria-label="Schedule history">
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="w-[190px]">Due (schedule time)</TableHead>
                  <TableHead className="w-[210px]">Status</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="w-[190px]">Resolved</TableHead>
                  <TableHead className="w-[120px]">Run</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {occurrences.map(o => (
                  <TableRow
                    key={o.id}
                    className={o.workflowRunId ? 'cursor-pointer' : undefined}
                    onClick={o.workflowRunId ? () => navigate(`/projects/${projectId}/workflows/runs/${o.workflowRunId}`) : undefined}
                    data-testid="schedule-occurrence-row"
                  >
                    <TableCell>{formatInTimeZone(o.scheduledForUtc, timeZoneId)}</TableCell>
                    <TableCell>
                      <OccurrenceStatusBadge status={o.status} reason={o.reason} />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5" data-testid="schedule-occurrence-reason">
                        <span className="min-w-0">{WORKFLOW_SCHEDULE_OCCURRENCE_REASON_LABELS[o.reason] ?? o.reason}</span>
                        {o.reasonDetail && (
                          <span className="min-w-0 text-xs text-muted-foreground">{o.reasonDetail}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{formatInTimeZone(o.resolvedAtUtc, timeZoneId)}</TableCell>
                    <TableCell>
                      {o.workflowRunId ? (
                        <Link
                          to={`/projects/${projectId}/workflows/runs/${o.workflowRunId}`}
                          className="text-sm font-medium text-primary hover:underline"
                          onClick={e => e.stopPropagation()}
                        >
                          View run
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            pageSizeOptions={pageSizeOptions}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      )}
    </section>
  )
}
